import React, { useContext } from "react";
import clsx from "clsx";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import ChatBubbleOutlineIcon from "@material-ui/icons/ChatBubbleOutline";
import ColorModeContext from "../../layout/themeContext";
import { i18n } from "../../translate/i18n";
import { getTokens, getNavTokens, brandAlpha, INK } from "../../styles/tokens";

/* Casca das telas de acesso (Login, Cadastro, Convite) no padrão do painel:
 * marca à esquerda (só ≥ md) num escuro derivado das cores da empresa e,
 * à direita, um cartão com o formulário. Nenhuma cor ou nome é fixo. */

const HERO_FALLBACK = {
  title: "Atendimento inteligente no WhatsApp para o seu negócio",
  subtitle:
    "Centralize conversas, automatize respostas e acompanhe sua equipe em um só lugar.",
};
const tr = (key, fallback) => i18n.t(key, { defaultValue: fallback });

const navOf = (theme) =>
  (theme.tokens && theme.tokens.nav) ||
  getNavTokens(theme.palette.primary.main, theme.palette.primary.main);

const useShellStyles = makeStyles((theme) => {
  const t = getTokens(theme.palette.type, theme.palette.primary.main);
  const nav = navOf(theme);
  const desktop = theme.breakpoints.up("md");
  return {
    root: {
      minHeight: "100vh",
      width: "100%",
      display: "grid",
      gridTemplateColumns: "minmax(0, 1fr)",
      fontFamily: t.font,
      backgroundColor: t.page,
      color: t.text,
      [desktop]: {
        gridTemplateColumns: "minmax(0, 5fr) minmax(0, 7fr)",
      },
    },

    /* ---------- painel da marca ---------- */
    brand: {
      display: "none",
      position: "relative",
      overflow: "hidden",
      isolation: "isolate",
      flexDirection: "column",
      justifyContent: "space-between",
      gap: 40,
      padding: 40,
      boxSizing: "border-box",
      color: nav.textStrong,
      backgroundColor: nav.bg,
      backgroundImage: `radial-gradient(ellipse at 0% 100%, ${brandAlpha(nav.accent, 0.3)}, transparent 60%)`,
      borderRight: `1px solid ${nav.line}`,
      [desktop]: { display: "flex" },
      // anel decorativo do painel, no destaque da marca
      "&::after": {
        content: '""',
        position: "absolute",
        zIndex: -1,
        width: 390,
        height: 390,
        right: -200,
        bottom: -150,
        borderRadius: "50%",
        border: `1px solid ${brandAlpha(nav.accent, 0.16)}`,
        boxShadow: `0 0 0 60px ${brandAlpha(nav.accent, 0.035)}, 0 0 0 120px ${brandAlpha(nav.accent, 0.02)}`,
        pointerEvents: "none",
      },
    },
    brandLogo: {
      display: "block",
      height: 36,
      width: "auto",
      maxWidth: 220,
      objectFit: "contain",
      objectPosition: "left center",
    },
    brandMark: {
      width: 40,
      height: 40,
      borderRadius: t.radius.sm,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      backgroundColor: nav.accentSoft,
      color: nav.accent,
    },
    brandEyebrow: {
      margin: "64px 0 0",
      fontSize: 12,
      fontWeight: 600,
      letterSpacing: "0.18em",
      textTransform: "uppercase",
      color: nav.accent,
      overflowWrap: "anywhere",
    },
    brandTitle: {
      margin: "20px 0 0",
      maxWidth: 460,
      fontSize: "clamp(30px, 3vw, 40px)",
      fontWeight: 600,
      lineHeight: 1.1,
      letterSpacing: "-0.03em",
      color: nav.textStrong,
      textWrap: "balance",
    },
    brandText: {
      margin: "24px 0 0",
      maxWidth: 400,
      fontSize: 17,
      lineHeight: 1.65,
      color: nav.text,
    },
    brandFooter: {
      margin: 0,
      fontSize: 13,
      color: nav.textMuted,
    },

    // Fundo configurado da marca: camada esmaecida sob o texto do painel. Nunca atrás do formulário —
    // quem cadastra uma logo como "fundo" via a logo gigante vazando por trás dos campos.
    brandPhoto: {
      position: "absolute",
      top: 0,
      right: 0,
      bottom: 0,
      left: 0,
      zIndex: -2,
      backgroundRepeat: "no-repeat",
      backgroundSize: "cover",
      backgroundPosition: "center center",
      opacity: 0.16,
      pointerEvents: "none",
    },

    /* ---------- lado do formulário ---------- */
    side: {
      minWidth: 0,
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      gap: 20,
      padding: "48px 24px",
      boxSizing: "border-box",
      [theme.breakpoints.down("xs")]: { padding: "32px 16px" },
    },
    card: {
      width: "100%",
      maxWidth: 440,
      padding: 40,
      boxSizing: "border-box",
      backgroundColor: t.paper,
      border: `1px solid ${t.line}`,
      borderRadius: t.radius.md,
      boxShadow: t.mode === "dark" ? t.shadow.soft : "0 18px 48px -38px rgba(16, 40, 63, 0.5)",
      [theme.breakpoints.down("xs")]: { padding: "28px 20px" },
    },
    cardWide: { maxWidth: 600 },
    cardLogo: {
      display: "block",
      height: 36,
      width: "auto",
      maxWidth: 200,
      objectFit: "contain",
      objectPosition: "left center",
      marginBottom: 24,
      [desktop]: { display: "none" },
    },
    cardMark: {
      width: 40,
      height: 40,
      marginBottom: 24,
      borderRadius: t.radius.sm,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      backgroundColor: t.tint.soft,
      color: t.brand,
      [desktop]: { display: "none" },
    },
    below: {
      width: "100%",
      maxWidth: 440,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      flexWrap: "wrap",
      gap: 8,
      fontSize: 12.5,
      color: t.textMuted,
    },
    belowWide: { maxWidth: 600 },
  };
});

/**
 * Props: `wide?` (cartão de 600 px, para formulário em 2 colunas), `below?` (linha abaixo do
 * cartão: versão, idioma, tema…), `backgroundImage?` (fundo configurado da marca; vai esmaecido no painel da marca).
 */
export const AuthShell = ({ wide = false, below, backgroundImage, children }) => {
  const classes = useShellStyles();
  const theme = useTheme();
  const { colorMode } = useContext(ColorModeContext);
  const { appName, mode } = colorMode;
  // sem nome configurado não há fallback fixo: some a sobrelinha e o rodapé
  const displayName = appName || "";

  // Sobre o painel escuro vai a logo do tema escuro; no cartão, a do tema em uso.
  const logoOnDark = theme.calculatedLogoDark ? theme.calculatedLogoDark() : colorMode.appLogoDark;
  const logoOnLight = theme.calculatedLogoLight ? theme.calculatedLogoLight() : colorMode.appLogoLight;
  const cardLogo = mode === "dark" ? logoOnDark : logoOnLight;

  return (
    <div className={classes.root}>
      <aside className={classes.brand}>
        {backgroundImage && (
          <span className={classes.brandPhoto} style={{ backgroundImage }} aria-hidden="true" />
        )}
        <div>
          {logoOnDark ? (
            <img src={logoOnDark} alt={displayName} className={classes.brandLogo} />
          ) : (
            <div className={classes.brandMark} aria-hidden="true">
              <ChatBubbleOutlineIcon style={{ fontSize: 20 }} />
            </div>
          )}
          {displayName && <p className={classes.brandEyebrow}>{displayName}</p>}
          <h2 className={classes.brandTitle} style={displayName ? undefined : { marginTop: 64 }}>
            {tr("login.hero.title", HERO_FALLBACK.title)}
          </h2>
          <p className={classes.brandText}>{tr("login.hero.subtitle", HERO_FALLBACK.subtitle)}</p>
        </div>
        {displayName && (
          <p className={classes.brandFooter}>
            © {new Date().getFullYear()} {displayName}
          </p>
        )}
      </aside>

      <main className={classes.side}>
        <div className={clsx(classes.card, wide && classes.cardWide)}>
          {cardLogo ? (
            <img src={cardLogo} alt={displayName} className={classes.cardLogo} />
          ) : (
            <div className={classes.cardMark} aria-hidden="true">
              <ChatBubbleOutlineIcon style={{ fontSize: 20 }} />
            </div>
          )}
          {children}
        </div>
        {below && <div className={clsx(classes.below, wide && classes.belowWide)}>{below}</div>}
      </main>
    </div>
  );
};

/** Estilos dos formulários de acesso: cabeçalho do cartão, campo de 48 px com ícone, botão largo, links. */
export const useAuthStyles = makeStyles((theme) => {
  const t = getTokens(theme.palette.type, theme.palette.primary.main);
  const dark = t.mode === "dark";
  return {
    eyebrow: {
      margin: 0,
      fontSize: 11,
      fontWeight: 600,
      letterSpacing: "0.12em",
      textTransform: "uppercase",
      color: t.brand,
      overflowWrap: "anywhere",
    },
    title: {
      margin: "12px 0 0",
      fontSize: 28,
      fontWeight: 600,
      lineHeight: 1.2,
      letterSpacing: "-0.02em",
      color: t.text,
      [theme.breakpoints.down("xs")]: { fontSize: 24 },
    },
    subtitle: {
      margin: "8px 0 0",
      fontSize: 15,
      lineHeight: 1.5,
      color: t.textSecondary,
    },
    strong: { fontWeight: 600, color: t.text },
    form: { marginTop: 28, display: "grid", gap: 18 },
    grid: {
      display: "grid",
      gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
      columnGap: 16,
      rowGap: 18,
      alignItems: "start",
      [theme.breakpoints.down("xs")]: { gridTemplateColumns: "minmax(0, 1fr)" },
    },
    full: { gridColumn: "1 / -1" },
    field: { display: "flex", flexDirection: "column", gap: 7, minWidth: 0 },
    label: {
      fontSize: 13.5,
      fontWeight: 500,
      lineHeight: 1.35,
      color: dark ? t.label : INK[700],
    },
    // o tema cuida de borda, raio e anel de foco; aqui só altura e ícone
    textField: {
      margin: 0,
      "& .MuiOutlinedInput-root": { minHeight: 48 },
      "& .MuiOutlinedInput-input": { padding: "14px 14px", fontSize: 15 },
      "& .MuiOutlinedInput-adornedStart .MuiOutlinedInput-input": { paddingLeft: 0 },
      "& .MuiOutlinedInput-adornedEnd .MuiOutlinedInput-input": { paddingRight: 0 },
      "& .MuiOutlinedInput-adornedStart": { paddingLeft: 14 },
      "& .MuiSelect-select.MuiOutlinedInput-input": { paddingRight: 36 },
      "& .MuiInputAdornment-positionStart": { marginRight: 11, color: t.textMuted },
      "& .MuiFormHelperText-root": { marginLeft: 2, marginRight: 0 },
    },
    fieldIcon: { fontSize: 20, color: t.textMuted },
    eyeBtn: { padding: 7, color: t.textMuted },
    help: { fontSize: 12, lineHeight: 1.45, color: t.textSecondary },
    submit: {
      minHeight: 48,
      padding: "10px 20px",
      fontSize: 15,
      "& .MuiButton-label": { justifyContent: "space-between" },
    },
    submitBusy: { "& .MuiButton-label": { justifyContent: "center" } },
    links: { display: "flex", justifyContent: "center", marginTop: 4, textAlign: "center" },
    link: {
      color: t.brand,
      fontWeight: 600,
      fontSize: 13.5,
      textDecoration: "none",
      "&:hover": { textDecoration: "underline" },
    },
    terms: { alignItems: "flex-start", marginLeft: -9, marginRight: 0 },
    termsLabel: { fontSize: 13.5, lineHeight: 1.5, color: t.textSecondary, paddingTop: 9 },
    state: {
      marginTop: 28,
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      textAlign: "center",
      gap: 10,
      padding: "8px 0",
    },
    stateIcon: {
      width: 48,
      height: 48,
      borderRadius: t.radius.md,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      backgroundColor: t.tint.soft,
      color: t.brand,
      "& .MuiSvgIcon-root": { fontSize: 24 },
    },
    stateTitle: { margin: 0, fontSize: 16, fontWeight: 600, color: t.text },
    stateText: { margin: 0, maxWidth: 340, fontSize: 13.5, lineHeight: 1.5, color: t.textSecondary },
  };
});

export default AuthShell;
